/**
 * Singleton managing the Twilio Voice Device lifecycle.
 *
 * Plain JS class with an event-emitter pattern so it works with Vue 3 Options
 * API components (no reactive refs — components subscribe via on/off).
 *
 * Lifecycle: initialize() creates the Device but does NOT register it.
 * Call register()/unregister() to control availability for inbound calls.
 *
 * Cross-tab sync: status changes (available/offline/busy) are broadcast via
 * BroadcastChannel so all open tabs show the same state.
 *
 * Usage:
 *   import { twilioDevice } from '@/services/TwilioDeviceService'
 *   await twilioDevice.initialize()       // after auth
 *   await twilioDevice.register()         // user goes "available"
 *   twilioDevice.on('incoming', handler)  // in component mounted()
 */
import { z } from 'zod'
import {
  getInboundCallByCallSid,
  getOutboundCallStatus,
  getVoiceToken,
  getUnreadVoicemailCount,
  lookupByPhone,
  markProviderDecline,
} from '@/services/api/voiceApi'
import { BrowserStore } from '@/utils/BrowserStore'

const STATUS_CHANNEL = 'twilio-phone-status'
const VOICEMAIL_POLL_INTERVAL = 120000
const INBOUND_SESSION_RETRY_MS = 500
const INBOUND_SESSION_MAX_ATTEMPTS = 10
const PHONE_SESSION_RETRY_MS = 500
const PHONE_SESSION_MAX_ATTEMPTS = 20

const voicemailStore = new BrowserStore(
  'unreadVoicemailCache',
  z.object({ count: z.number().default(0), ts: z.number().default(0) }),
)

const phoneAvailabilityStore = new BrowserStore(
  'twilioPhoneAvailability',
  z.object({ available: z.boolean().default(false) }),
)

const phoneStatusStore = new BrowserStore(
  'twilioPhoneStatus',
  z.object({ status: z.string().default('offline') }),
)

class TwilioDeviceService {
  constructor() {
    /** @type {Device | null} */
    this.device = null
    /** @type {import('@twilio/voice-sdk').Call | null} */
    this.activeCall = null
    this.currentCallContext = null
    /** @type {string | null} */
    this.identity = null
    this._listeners = {}
    this._tokenRefreshTimer = null
    this._initialized = false
    this._registered = false
    this._isDestroying = false
    this._initializationPromise = null
    this._registrationPromise = null
    this._recoveryPromise = null
    this._autoRecoveryAttempted = false

    /** @type {'offline' | 'available' | 'busy'} */
    this._status = 'offline'
    this._unreadVoicemails = voicemailStore.load().count
    this._voicemailPollTimer = null
    this._channel = new BroadcastChannel(STATUS_CHANNEL)
    this._channel.onmessage = (e) => this._onCrossTabMessage(e.data)
    this._onStorageEvent = (e) => {
      if (e.key === 'twilioPhoneStatus' && e.newValue) {
        try {
          const { status } = JSON.parse(e.newValue)
          if (status && status !== this._status && !this.activeCall) {
            this._status = status
            this._emit('statusChanged', status)
          }
        } catch {
          // ignore
        }
      }
      if (e.key !== 'unreadVoicemailCache' || !e.newValue) return
      try {
        const { count } = JSON.parse(e.newValue)
        if (typeof count === 'number' && count !== this._unreadVoicemails) {
          this._unreadVoicemails = count
          this._emit('unreadVoicemailsChanged', count)
        }
      } catch {
        // ignore
      }
    }
    window.addEventListener('storage', this._onStorageEvent)
  }

  // ─── Event emitter ──────────────────────────────────────────────────────

  /**
   * @param {string} event
   * @param {Function} fn
   */
  on(event, fn) {
    if (!this._listeners[event]) this._listeners[event] = []
    this._listeners[event].push(fn)
  }

  /**
   * @param {string} event
   * @param {Function} fn
   */
  off(event, fn) {
    const list = this._listeners[event]
    if (!list) return
    this._listeners[event] = list.filter((f) => f !== fn)
  }

  /** @param {string} event @param {...any} args */
  _emit(event, ...args) {
    const list = this._listeners[event]
    if (!list) return
    for (const fn of list) {
      try {
        fn(...args)
      } catch (e) {
        console.error(`[TwilioDevice] listener error on "${event}":`, e)
      }
    }
  }

  // ─── Cross-tab status sync ──────────────────────────────────────────────

  /** @returns {'offline' | 'available' | 'busy'} */
  get status() {
    return this._status
  }

  _setStatus(status, { sync = !this._isDestroying } = {}) {
    if (this._status === status) return
    // Never downgrade from busy while a call is active
    if (this._status === 'busy' && status !== 'busy' && this.activeCall) return
    this._status = status
    if (sync) {
      phoneStatusStore.save({ status })
      this._channel.postMessage({ type: 'status', status })
    }
    this._emit('statusChanged', status)
  }

  // ─── Unread voicemail count ──────────────────────────────────────────

  get unreadVoicemails() {
    return this._unreadVoicemails
  }

  _setUnreadVoicemails(count) {
    if (this._unreadVoicemails === count) return
    this._unreadVoicemails = count
    voicemailStore.save({ count, ts: Date.now() })
    this._emit('unreadVoicemailsChanged', count)
  }

  async fetchUnreadVoicemails() {
    try {
      const count = await getUnreadVoicemailCount()
      this._setUnreadVoicemails(count)
    } catch {
      // silently ignore — polling will retry
    }
  }

  startVoicemailPolling() {
    this.stopVoicemailPolling()
    const cached = voicemailStore.load()
    if (Date.now() - cached.ts < VOICEMAIL_POLL_INTERVAL) {
      this._unreadVoicemails = cached.count
      this._emit('unreadVoicemailsChanged', cached.count)
    } else {
      this.fetchUnreadVoicemails()
    }
    this._voicemailPollTimer = setInterval(
      () => this.fetchUnreadVoicemails(),
      VOICEMAIL_POLL_INTERVAL,
    )
  }

  stopVoicemailPolling() {
    if (this._voicemailPollTimer) {
      clearInterval(this._voicemailPollTimer)
      this._voicemailPollTimer = null
    }
  }

  _broadcastCallContext() {
    if (this.activeCall && this.currentCallContext) {
      const context = {
        callerNumber: this.activeCall.parameters?.From
          || this.activeCall.customParameters?.get('to_phone_number')
          || '',
        workId: this.currentCallContext.workId,
        entityName: this.currentCallContext.entityName,
        sessionUuid: this.currentCallContext.sessionUuid || null,
      }
      try { localStorage.setItem('twilioActiveCallContext', JSON.stringify(context)) } catch {}
      this._channel.postMessage({ type: 'callContext', context })
    } else {
      try { localStorage.removeItem('twilioActiveCallContext') } catch {}
      this._channel.postMessage({ type: 'callContext', context: null })
    }
  }

  requestFocusCallTab() {
    this._channel.postMessage({ type: 'command', action: 'focus' })
  }

  _onCrossTabMessage(data) {
    if (data.type === 'status' && data.status !== this._status) {
      // Don't let another tab override busy while we have an active call
      if (this._status === 'busy' && data.status !== 'busy' && this.activeCall) return
      this._status = data.status
      this._emit('statusChanged', data.status)
    }
    if (data.type === 'callContext') {
      if (!this.activeCall) {
        this._emit('crossTabCall', data.context)
      }
    }
    if (data.type === 'command') {
      if (data.action === 'focus' && this.activeCall) {
        window.focus()
      }
      if (data.action === 'register' && this._initialized && !this._registered) {
        this.register()
      }
      if (data.action === 'unregister' && this._registered) {
        this.unregister()
      }
      if (data.action === 'rebroadcastContext' && this.activeCall) {
        this._broadcastCallContext()
      }
    }
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  async initialize({ force = false, restoreAvailability = true } = {}) {
    if (this._initialized && !force) return
    if (this._initializationPromise) return this._initializationPromise

    this._initializationPromise = this._initialize({ force, restoreAvailability })
    try {
      await this._initializationPromise
    } finally {
      this._initializationPromise = null
    }
  }

  async _initialize({ force, restoreAvailability }) {
    this._isDestroying = false
    if (force) this._discardDevice()
    try {
      const { token, identity } = await getVoiceToken()
      this.identity = identity

      const { Device } = await import('@twilio/voice-sdk')
      this.device = new Device(token, {
        edge: 'ashburn',
        closeProtection: true,
        logLevel: 'warn',
        codecPreferences: ['opus', 'pcmu'],
        allowIncomingWhileBusy: true,
      })
      this._bindDeviceEvents()
      this._initialized = true
      this.startVoicemailPolling()

      const stored = phoneAvailabilityStore.load()
      if (restoreAvailability && stored.available) {
        // Loading the application is not a user gesture. Surface the failure
        // through deviceError but avoid an unhandled rejected promise.
        this.register().catch(() => {})
      }
    } catch (e) {
      console.error('[TwilioDevice] initialization failed:', e)
      this._emit('deviceError', e)
      throw e
    }
  }

  _discardDevice() {
    const device = this.device
    // Clear the reference before destroying the Device. Its asynchronous
    // events then cannot overwrite the state of a newly-created Device.
    this.device = null
    this._initialized = false
    this._registered = false
    if (!device) return
    try {
      device.destroy()
    } catch (e) {
      console.warn('[TwilioDevice] could not discard stale device:', e)
    }
  }

  _markRegistered() {
    const wasRegistered = this._registered
    this._registered = true
    const shared = phoneStatusStore.load().status
    this._setStatus(shared === 'busy' ? 'busy' : 'available')
    phoneAvailabilityStore.save({ available: true })
    if (!wasRegistered) {
      this._emit('deviceRegistered', { identity: this.identity })
    }
  }

  _reportRegistrationFailure(error) {
    console.error('[TwilioDevice] registration failed:', error)
    console.error('[TwilioDevice] registration error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      causes: error.causes,
    })
    // Do not broadcast this local failure: another tab can still have a
    // working registration for the same user.
    this._setStatus('offline', { sync: false })
    this._emit('deviceError', error)
  }

  async register() {
    if (this._registered) {
      this._markRegistered()
      return
    }
    if (this._registrationPromise) return this._registrationPromise

    // A manual retry gets one fresh Device even if the previous automatic
    // recovery also failed.
    this._autoRecoveryAttempted = false
    this._registrationPromise = this._register({ retryWithFreshDevice: true })
    try {
      await this._registrationPromise
    } finally {
      this._registrationPromise = null
    }
  }

  async _register({ retryWithFreshDevice }) {
    try {
      if (!this.device) await this.initialize()
      if (!this.device) throw new Error('Twilio Device could not be initialized')
      await this.device.register()
      this._markRegistered()
    } catch (error) {
      if (retryWithFreshDevice && !this.activeCall) {
        console.warn('[TwilioDevice] retrying registration with a fresh token')
        try {
          await this.initialize({ force: true, restoreAvailability: false })
          return await this._register({ retryWithFreshDevice: false })
        } catch (retryError) {
          this._reportRegistrationFailure(retryError)
          throw retryError
        }
      }
      this._reportRegistrationFailure(error)
      throw error
    }
  }

  async unregister() {
    if (!this.device || !this._registered) {
      this._channel.postMessage({ type: 'command', action: 'unregister' })
      return
    }
    try {
      this._registered = false
      this._setStatus('offline')
      phoneAvailabilityStore.save({ available: false })
      this.device.unregister()
      this._emit('deviceUnregistered')
    } catch (e) {
      console.error('[TwilioDevice] unregistration failed:', e)
    }
  }

  _bindDeviceEvents() {
    const device = this.device
    if (!device) return

    device.on('incoming', (call) => {
      if (this.device !== device) return
      if (this.activeCall) {
        call.reject()
        return
      }
      this.activeCall = call
      this._bindCallEvents(call)

      const callSid = call.parameters?.CallSid || null
      const fromNumber = call.parameters?.From

      this.currentCallContext = {
        direction: 'inbound',
        callSid,
        sessionUuid: null,
        workId: null,
        entityId: null,
        entityName: null,
        matchCount: 0,
        matchedEntities: [],
        phoneBookEntry: null,
        status: 'pending',
      }

      this._setStatus('busy')

      this._emit('incoming', {
        call,
        callSid,
        matchCount: 0,
        matchedEntities: [],
        fromNumber,
        lookupLoading: true,
      })

      if (fromNumber) {
        lookupByPhone(fromNumber).then((result) => {
          const phoneBookEntry = result.phone_book_entry || null
          const matchedEntities = phoneBookEntry ? [] : result.matches || []
          const matchCount = phoneBookEntry ? 0 : result.match_count || 0
          this.currentCallContext = {
            ...this.currentCallContext,
            entityId: matchCount === 1 ? matchedEntities[0]?.entity_id : null,
            entityName: matchCount === 1 ? matchedEntities[0]?.entity_name : null,
            matchCount,
            matchedEntities,
            phoneBookEntry,
          }
          this._emit('incomingLookupComplete', { matchCount, matchedEntities, phoneBookEntry })
        }).catch(() => {
          this._emit('incomingLookupComplete', { matchCount: 0, matchedEntities: [], phoneBookEntry: null })
        })
      } else {
        this._emit('incomingLookupComplete', { matchCount: 0, matchedEntities: [], phoneBookEntry: null })
      }
    })

    device.on('registered', () => {
      if (this.device === device) this._markRegistered()
    })

    device.on('tokenWillExpire', async () => {
      try {
        const { token } = await getVoiceToken()
        if (this.device === device) device.updateToken(token)
      } catch (e) {
        console.error('[TwilioDevice] token refresh failed:', e)
        this._emit('deviceError', e)
      }
    })

    device.on('error', (err) => {
      if (this.device !== device) return
      console.error('[TwilioDevice] device error:', err)
      console.error('[TwilioDevice] device error details:', {
        name: err.name,
        message: err.message,
        code: err.code,
        causes: err.causes,
      })
      this._emit('deviceError', err)
    })

    device.on('unregistered', () => {
      if (this.device !== device) return
      this._registered = false
      // A device can unregister because its tab is closing or it loses its
      // connection. That only affects this tab; only unregister() represents
      // the user's shared "go unavailable" action.
      this._setStatus('offline', { sync: false })
      this._emit('deviceUnregistered')
      this._recoverUnexpectedUnregistration()
    })

    device.on('destroyed', () => {
      if (this.device !== device) return
      this.device = null
      this._initialized = false
      this._registered = false
      this._setStatus('offline', { sync: false })
      this._recoverUnexpectedUnregistration()
    })
  }

  _recoverUnexpectedUnregistration() {
    if (
      this._isDestroying ||
      this.activeCall ||
      this._registrationPromise ||
      this._autoRecoveryAttempted ||
      !phoneAvailabilityStore.load().available
    ) return

    this._autoRecoveryAttempted = true
    this._recoveryPromise = (async () => {
      try {
        await this.initialize({ force: true, restoreAvailability: false })
        await this._register({ retryWithFreshDevice: false })
      } catch {
        // _register and initialize have already emitted a deviceError.
      } finally {
        this._recoveryPromise = null
      }
    })()
  }

  /** @param {import('@twilio/voice-sdk').Call} call */
  _bindCallEvents(call) {
    const guardUnload = () => {
      this._beforeUnloadHandler = (e) => { e.preventDefault() }
      window.addEventListener('beforeunload', this._beforeUnloadHandler)
    }
    const unguardUnload = () => {
      if (this._beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
        this._beforeUnloadHandler = null
      }
    }
    const cleanupCall = () => {
      this.activeCall = null
      this.currentCallContext = null
      this._setStatus(this._registered ? 'available' : 'offline')
      this._broadcastCallContext()
      unguardUnload()
    }

    call.on('accept', () => {
      if (this.currentCallContext) {
        this.currentCallContext.callSid = call.parameters?.CallSid || null
        this.currentCallContext.connectedAt = Date.now()
      }
      this._setStatus('busy')
      guardUnload()
      this._broadcastCallContext()
      this._emit('callConnected', call)
      if (this.currentCallContext?.direction === 'inbound') {
        this._resolveInboundSession(call)
      }
    })

    call.on('disconnect', () => {
      cleanupCall()
      this._emit('callDisconnected')
    })

    call.on('cancel', () => {
      let crossTabContext = null
      try {
        const stored = localStorage.getItem('twilioActiveCallContext')
        if (stored) crossTabContext = JSON.parse(stored)
      } catch {}

      this.activeCall = null
      this.currentCallContext = null
      this._broadcastCallContext()
      unguardUnload()
      this._setStatus(this._registered ? 'available' : 'offline')
      this._emit('callDisconnected')

      if (crossTabContext) {
        this._emit('crossTabCall', crossTabContext)
      }
      this._channel.postMessage({ type: 'command', action: 'rebroadcastContext' })
    })

    call.on('error', (err) => {
      console.error('[TwilioDevice] call error:', err)
      cleanupCall()
      this._emit('callError', err)
    })

    call.on('reject', () => {
      cleanupCall()
      this._emit('callDisconnected')
    })

    call.on('ringing', () => {
      this._emit('callRinging', call)
    })
  }

  // ─── Outbound ───────────────────────────────────────────────────────────

  /**
   * Place an outbound call via the Twilio Device.
   * @param {{ toPhoneNumber?: string, fromPhoneNumber: string, workId: string | number, entityId?: number | string | null, entityName?: string | null, phoneBookEntry?: import('./api/schema').components['schemas']['PhoneBookEntry'] | null, roleId?: number | null, contactLabel?: string | null, workRouteName?: string }} params
   * @returns {Promise<import('@twilio/voice-sdk').Call>}
   */
  async connect({ toPhoneNumber, fromPhoneNumber, workId, entityId, entityName, phoneBookEntry, roleId, contactLabel, workRouteName }) {
    if (!this.device) {
      throw new Error('Twilio Device not initialized')
    }
    if (this.activeCall) {
      throw new Error('A call is already active')
    }

    if (!this._registered) {
      await this.register()
    }

    const params = {
      to_phone_number: toPhoneNumber || phoneBookEntry?.phone_number,
      from_phone_number: fromPhoneNumber,
      work_id: String(workId),
    }
    if (phoneBookEntry) params.phone_book_entry_id = String(phoneBookEntry.id)
    else if (roleId) params.role_id = String(roleId)

    const call = await this.device.connect({ params })

    this.activeCall = call
    this._bindCallEvents(call)

    this.currentCallContext = {
      direction: 'outbound',
      callSid: null,
      fromPhoneNumber: fromPhoneNumber || null,
      workId: workId || null,
      entityId: entityId || null,
      entityName: entityName || null,
      matchCount: -1,
      matchedEntities: [],
      phoneBookEntry: phoneBookEntry || null,
      contactLabel: contactLabel || null,
      workRouteName: workRouteName || 'ClinicalEngagement',
      status: workId ? 'linked' : 'pending',
    }

    this._setStatus('busy')
    this._emit('callConnecting', call)
    return call
  }

  // ─── Inbound ────────────────────────────────────────────────────────────

  /** @param {import('@twilio/voice-sdk').Call} call */
  acceptIncoming(call) {
    call.accept()
  }

  /** @param {import('@twilio/voice-sdk').Call} call */
  async rejectIncoming(call) {
    const callSid = call.parameters?.CallSid
    if (callSid) {
      try {
        await markProviderDecline(callSid)
      } catch (e) {
        // The SDK still needs to reject the call if recording intent fails.
        // The backend will fall back to its normal busy handling in that case.
        console.error('[TwilioDevice] could not record provider decline:', e)
      }
    }
    call.reject()
    if (this.activeCall === call) {
      this.activeCall = null
    }
  }

  // ─── Phone session (conference) ─────────────────────────────────────────

  /**
   * Resolve the active call's phone session, retrying while the backend creates
   * it. Inbound sessions are filled in by _resolveInboundSession; outbound ones
   * are looked up by the provider leg's CallSid.
   * @returns {Promise<string | null>} the phone session uuid, or null if the call ended or it never appeared
   */
  async resolvePhoneSessionUuid() {
    const call = this.activeCall
    for (let attempt = 0; attempt < PHONE_SESSION_MAX_ATTEMPTS; attempt++) {
      const ctx = this.currentCallContext
      if (!call || this.activeCall !== call || !ctx) return null
      if (ctx.phoneSessionUuid) return ctx.phoneSessionUuid
      if (ctx.direction === 'outbound' && ctx.callSid) {
        try {
          const status = await getOutboundCallStatus(ctx.callSid)
          if (status?.phone_session_uuid && this.currentCallContext === ctx) {
            ctx.phoneSessionUuid = status.phone_session_uuid
            return ctx.phoneSessionUuid
          }
        } catch {
          // session not created yet
        }
      }
      await new Promise((resolve) => setTimeout(resolve, PHONE_SESSION_RETRY_MS))
    }
    return null
  }

  // ─── Inbound call session ───────────────────────────────────────────────

  /**
   * The browser only knows its own call leg's CallSid; the backend maps it to
   * the inbound phone session. The provider participant row is written right
   * after Twilio accepts the REST add, so the first lookups can miss.
   */
  /** @param {import('@twilio/voice-sdk').Call} call */
  async _resolveInboundSession(call) {
    const callSid = call.parameters?.CallSid
    if (!callSid) return
    for (let attempt = 0; attempt < INBOUND_SESSION_MAX_ATTEMPTS; attempt++) {
      if (this.activeCall !== call) return
      try {
        const inboundCall = await getInboundCallByCallSid({ call_sid: callSid })
        if (this.activeCall !== call || !this.currentCallContext) return
        this.currentCallContext.sessionUuid = inboundCall.uuid
        // Conference controls need the phone session and the Kairon number that
        // was dialled: it is the caller ID for anyone added to this call.
        this.currentCallContext.phoneSessionUuid = inboundCall.uuid
        this.currentCallContext.fromPhoneNumber = inboundCall.to_number || null
        this._broadcastCallContext()
        this._emit('inboundCallResolved', { sessionUuid: inboundCall.uuid })
        return
      } catch (e) {
        if (e?.response?.status !== 404) {
          console.error('[TwilioDevice] could not resolve inbound call session:', e)
          break
        }
      }
      await new Promise((resolve) => setTimeout(resolve, INBOUND_SESSION_RETRY_MS))
    }
    if (this.activeCall === call) {
      this._emit('inboundCallUnresolved')
    }
  }

  // ─── Engagement linking ──────────────────────────────────────────────────

  linkEngagement({ workId, entityId, entityName }) {
    if (this.currentCallContext) {
      this.currentCallContext.workId = workId
      this.currentCallContext.entityId = entityId
      this.currentCallContext.entityName = entityName
      this.currentCallContext.status = 'linked'
      this._broadcastCallContext()
      this._emit('engagementLinked', this.currentCallContext)
    }
  }

  // ─── Call controls ──────────────────────────────────────────────────────

  toggleMute() {
    if (!this.activeCall) return false
    const muted = !this.activeCall.isMuted()
    this.activeCall.mute(muted)
    this._emit('muteChanged', muted)
    return muted
  }

  /** @param {string} digits */
  sendDigits(digits) {
    if (!this.activeCall) return
    this.activeCall.sendDigits(digits)
  }

  disconnect() {
    if (this.activeCall) {
      this.activeCall.disconnect()
      this.activeCall = null
      this.currentCallContext = null
    }
  }

  // ─── Media streams (for TranscriptionEngine) ────────────────────────────

  getLocalStream() {
    return this.activeCall?.getLocalStream() ?? null
  }

  getRemoteStream() {
    return this.activeCall?.getRemoteStream() ?? null
  }

  // ─── Teardown ───────────────────────────────────────────────────────────

  async destroy() {
    // destroy() tears down this tab (for example, during logout). It must not
    // make another open tab unavailable through the shared status channel.
    this._isDestroying = true
    this.disconnect()
    this.stopVoicemailPolling()
    window.removeEventListener('storage', this._onStorageEvent)
    if (this.device) {
      this.device.unregister()
      this.device.destroy()
      this.device = null
    }
    this._initialized = false
    this._registered = false
    this._setStatus('offline')
    this._unreadVoicemails = 0
    this.identity = null
    this.currentCallContext = null
    this._listeners = {}
  }

  get isInitialized() {
    return this._initialized
  }

  get isRegistered() {
    return this._registered
  }

  get isBusy() {
    return this.activeCall !== null
  }
}

export const twilioDevice = new TwilioDeviceService()
